/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useState } from 'react'
import { useSelector } from 'react-redux'
// utils

// material
import { Page, Content, Division, InContentMessage } from '@insticator/insticator-ui'
import Header from '../../../../material/structure/header/header'
import GlobalFilter from '../../../../material/globalfilter/globalfilter'
import PageHeader from '../../../../material/structure/pageheader/pageheader'

// styles
import injectSheet from 'react-jss'
import styles from './fulladmanagement_styles'
import RevenueVisualization from '../revenue_beta/revenue_beta'

// static
import {
  NET_REVENUE,
  GROSS_REVENUE,
  AVG_RPM,
  AVG_CPM,
  SITE,
  DEVICE,
  GEO,
  PRODUCT_TYPE,
  PAGE_VIEWS,
  TOTAL_IMPRESSIONS,
  BAR_CHART,
  LINE_CHART,
  INITIAL_IMPRESSIONS,
  REFRESH_IMPRESSIONS, VIEWABILITY, AD_UNIT, INITIAL_VIEWABILITY, REFRESH_VIEWABILITY, IMPRESSION_PER_PAGEVIEW,
  TOOLTIP_CONTENTS, PAGEVIEW_TYPE
} from '../consts'

/* ========== ~~~~~~~~~~ MONETIZATION - FULL AD MANAGEMENT PAGE ~~~~~~~~~~ ========== */
const FullAdManagementPage = injectSheet(styles)((props) => {
  const accountUUID = useSelector((state) => state.session.accountUUID)
  const startDate = useSelector((state) => state.app.filters.dateRange.from)
  const endDate = useSelector((state) => state.app.filters.dateRange.to)
  const compareTo = useSelector((state) => state.app.filters.compareTo)
  const devices = useSelector((state) => state.app.filters.devices)
  const countries = useSelector((state) => state.app.filters.countries)
  const siteUUIDs = useSelector((state) => state.app.filters.sites)
  const lastUpdated = useSelector((state) => state.revenueBeta.lastUpdated)
  const mocked = useSelector((state) => state.session.mocked)
  const { showMessage, message } = useSelector((state) => state.settings.notificationsConfig)

  const { enableCpmAdMgmt, enableInternalMetrics, enableAdUnitUnderCpmAdMgmt } = useSelector((state) => state.account.accountSettings)

  const [from, setFrom] = useState(startDate)
  const [to, setTo] = useState(endDate)
  const [compare, setCompare] = useState(compareTo)

  const cardtoggles = {
    revenue: [
      { id: NET_REVENUE, label: 'Publisher Revenue' }
    ],
    rpmCpm: [
      { id: AVG_RPM, label: 'Average RPM' }
    ],
    pageview: [
      { id: PAGE_VIEWS, label: 'Monetizable Page Views' }
    ],
    impression: [
      { id: TOTAL_IMPRESSIONS, label: 'Total Impressions' }
    ],
    viewability: [
      { id: VIEWABILITY, label: 'Avg Ad Viewability' }
    ]
  }
  const baseParams = type => {
    const productType = type === 'pageview' ? PAGEVIEW_TYPE : PRODUCT_TYPE
    const productTypeFilter = type === 'pageview' ? [AD_UNIT] : ['STANDALONE', 'ANCHOR', 'INTERSTITIAL']
    return {
      accountUUID,
      startDate: from,
      endDate: to,
      filters: {
        [SITE]: siteUUIDs.length ? siteUUIDs : undefined,
        [DEVICE]: devices.length ? devices : undefined,
        [GEO]: countries.length ? countries : undefined,
        [productType]: productTypeFilter
      },
      limit: 4,
      compareTo: compare
    }
  }
  const params = {
    revenue: {
      ...baseParams('revenue'),
      dimension: PRODUCT_TYPE,
      metrics: [NET_REVENUE],
      initialMetric: NET_REVENUE
    },
    rpmCpm: {
      ...baseParams('pageview'),
      dimension: SITE,
      metrics: [AVG_RPM],
      initialMetric: AVG_RPM
    },
    pageview: {
      ...baseParams('pageview'),
      dimension: DEVICE,
      metrics: [PAGE_VIEWS],
      initialMetric: PAGE_VIEWS
    },
    impression: {
      ...baseParams('revenue'),
      dimension: SITE,
      metrics: [TOTAL_IMPRESSIONS],
      initialMetric: TOTAL_IMPRESSIONS
    },
    viewability: {
      ...baseParams('revenue'),
      dimension: AD_UNIT,
      metrics: [VIEWABILITY],
      initialMetric: VIEWABILITY
    }
  }
  const allowedDimensions = {
    revenue: [SITE, DEVICE, GEO, PRODUCT_TYPE, AD_UNIT],
    rpmCpm: [SITE, DEVICE, GEO],
    pageview: [SITE, DEVICE, GEO],
    impression: [SITE, DEVICE, GEO],
    viewability: [SITE, DEVICE, GEO, PRODUCT_TYPE, AD_UNIT]
  }

  // internal only
  if (enableInternalMetrics && mocked) {
    cardtoggles.revenue.push({ id: GROSS_REVENUE, label: 'Gross Revenue' })
    cardtoggles.impression.push({ id: INITIAL_IMPRESSIONS, label: 'Initial Load Impressions' })
    cardtoggles.impression.push({ id: REFRESH_IMPRESSIONS, label: 'Refresh Impressions' })
    cardtoggles.viewability.push({ id: INITIAL_VIEWABILITY, label: 'Initial Load Viewability' })
    cardtoggles.viewability.push({ id: REFRESH_VIEWABILITY, label: 'Refresh Viewability' })

    params.revenue.metrics.push(GROSS_REVENUE)
    params.impression.metrics.push(INITIAL_IMPRESSIONS)
    params.impression.metrics.push(REFRESH_IMPRESSIONS)
    params.viewability.metrics.push(INITIAL_VIEWABILITY)
    params.viewability.metrics.push(REFRESH_VIEWABILITY)
  }

  if (enableCpmAdMgmt) {
    cardtoggles.rpmCpm.push({ id: AVG_CPM, label: 'Average CPM' })
    params.rpmCpm.metrics.push(AVG_CPM)
    if (enableAdUnitUnderCpmAdMgmt)// check if enable ad unit toggle is on for cpm dropdown for ad management page
    {
      allowedDimensions.rpmCpm.push(AD_UNIT)/* pusing AD_UNIT when CPM is enabled for publisher on engagment product page */
    }
  } else {
    delete cardtoggles.impression
    delete params.impression
  }

  return (
        <Division name={'FullAdManagement'}>
            <Page name={'FullAdManagement'} klass={`${props.classes.FullAdManagement}`} >
                <Header>
                    <GlobalFilter onFilterApply={() => {
                      setFrom(startDate)
                      setTo(endDate)
                      setCompare(compareTo)
                    }} />
                </Header>
                <Content>
                    <InContentMessage />
                    <PageHeader lastUpdatedTimestamp={lastUpdated}
                                title={'Monetization'}
                                subtitle={'Full Ad Management'}
                                dataTip={true}
                                bannerText={showMessage ? message : null}
                                ignoreDataTip={showMessage}
                    />
                    <RevenueVisualization
                        params={params.revenue}
                        cardtogglesList={cardtoggles.revenue}
                        chartType={BAR_CHART}
                        allowedDimensions={allowedDimensions.revenue}
                        title={cardtoggles.revenue[0].label}
                        tooltipContent ={TOOLTIP_CONTENTS.revenue}
                    />
                    <RevenueVisualization
                        params={params.rpmCpm}
                        cardtogglesList={cardtoggles.rpmCpm}
                        chartType={LINE_CHART}
                        allowedDimensions={allowedDimensions.rpmCpm}
                        title={cardtoggles.rpmCpm[0].label}
                        tooltipContent ={TOOLTIP_CONTENTS.rpmCpm}
                    />
                    <RevenueVisualization
                        params={params.pageview}
                        cardtogglesList={cardtoggles.pageview}
                        chartType={BAR_CHART}
                        allowedDimensions={allowedDimensions.pageview}
                        title={cardtoggles.pageview[0].label}
                        tooltipContent ={TOOLTIP_CONTENTS.pageview}
                    />
                    {enableCpmAdMgmt && <RevenueVisualization
                        params={params.impression}
                        cardtogglesList={cardtoggles.impression}
                        chartType={BAR_CHART}
                        allowedDimensions={allowedDimensions.impression}
                        title={cardtoggles.impression[0].label}
                        tooltipContent={TOOLTIP_CONTENTS.impression}
                        zAxisId={IMPRESSION_PER_PAGEVIEW}
                        zAxisLabel={'Impressions per Pageview'}
                    />}
                    <RevenueVisualization
                        params={params.viewability}
                        cardtogglesList={cardtoggles.viewability}
                        chartType={LINE_CHART}
                        allowedDimensions={allowedDimensions.viewability}
                        title={cardtoggles.viewability[0].label}
                        tooltipContent ={TOOLTIP_CONTENTS.viewability}
                    />
                </Content>
            </Page>
        </Division>
  )
})

/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default FullAdManagementPage
